import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "211",
  "slug": "satellite-imagery-education-teaching-geography",
  "title": "Teaching Geography With Satellite Images",
  "excerpt": "Build a lesson around a river, a field pattern, or a city edge. Help students separate what an image shows from what they need other evidence to explain.",
  "date": "2026-03-05",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?w=800&q=80",
  "readTime": "3 min",
  "content": [
    {
      "type": "paragraph",
      "text": "A satellite image of a river mouth lets a class follow the channel into the sea, trace islands, and see where nearby settlements sit. A diagram can label the process of deposition. Use the two together and ask students which parts of the diagram they can identify in the image."
    },
    {
      "type": "paragraph",
      "text": "Choose one feature for the lesson. An image packed with unfamiliar detail can leave students guessing what they are supposed to notice."
    },
    {
      "type": "heading",
      "text": "Separate observation from explanation"
    },
    {
      "type": "paragraph",
      "text": "Make two columns: “visible in the image” and “possible explanation.” Students might observe a sharp boundary between green fields and bare ground, then suggest irrigation as an explanation. They would need more information to establish where the water comes from."
    },
    {
      "type": "paragraph",
      "text": "This distinction also applies to cities. An image may show a dense group of buildings beside a road junction. It cannot, on its own, tell you residents' incomes or why they chose to live there."
    },
    {
      "type": "heading",
      "text": "Check the date and scale"
    },
    {
      "type": "paragraph",
      "text": "Find the acquisition date if the image source provides it. A harvested field and the same field during the growing season may look quite different. Comparing images from different months can introduce seasonal changes into what you intended as a comparison of land use."
    },
    {
      "type": "paragraph",
      "text": "Keep the scale similar when comparing two places. If one image covers a neighbourhood and another covers a province, differences in visible detail may come from the zoom level. Include a scale bar when possible."
    },
    {
      "type": "heading",
      "text": "A 30-minute lesson outline"
    },
    {
      "type": "list",
      "items": [
        "Spend five minutes collecting observations from an unlabelled image.",
        "Give pairs five minutes to propose a location or explanation and record their evidence.",
        "Reveal the location and spend ten minutes comparing the image with a map or diagram tied to the lesson.",
        "Use the final ten minutes for a second image and an individual written explanation."
      ]
    },
    {
      "type": "paragraph",
      "text": "You can use a satellite geography game for the guessing stage. Prepare a separate image when the lesson requires a particular feature or date, so the teaching objective does not depend on a random round."
    },
    {
      "type": "heading",
      "text": "Ask questions the image can help answer"
    },
    {
      "type": "paragraph",
      "text": "For a delta, ask where the channels split and where sediment is visible. For farmland, compare field sizes and the position of roads. For an urban edge, ask students to trace where continuous development gives way to fields or open land."
    },
    {
      "type": "paragraph",
      "text": "Then ask what is missing. A rainfall record can help explain vegetation; a historical map can help explain a street pattern. Students should be able to name the extra evidence they would need before treating their interpretation as a conclusion."
    },
    {
      "type": "heading",
      "text": "Assess an explanation on a new image"
    },
    {
      "type": "paragraph",
      "text": "Use a different view of the same kind of feature for the exit task. Ask students to mark it and explain how they recognised it. Keep the original image available for comparison if the goal is interpretation rather than recall."
    }
  ],
  "tldr": "Satellite images let students examine the arrangement of landforms and land use. Choose an image tied to one lesson objective, ask for observations before explanations, and use maps or other data to check their interpretations."
};
